import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getCmsData } from '../cms/cmsData';

const SITE_URL = 'https://www.ocmenafestival.com';
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const LOGO_URL = `${SITE_URL}/images/logo-m.png`;

/* ── Map URL paths → CMS seoMetadata.pages keys ──────── */
const PATH_TO_CMS_KEY = {
  '/': 'home',
  '/tickets': 'tickets',
  '/festival-tickets': 'festivalTickets',
  '/amphitheater-tickets': 'amphitheaterTickets',
  '/amphitheater-tickets-saturday': 'amphitheaterTickets',
  '/amphitheater-tickets-sunday': 'amphitheaterTickets',
  '/amphitheater-tickets-v2': 'amphitheaterTickets',
  '/vendors': 'vendors',
  '/bazaar-vendor': 'bazaarVendor',
  '/food-vendor': 'foodVendor',
  '/about': 'about',
  '/what-is-mena': 'whatIsMena',
  '/faq': 'faq',
  '/festival': 'festivalInfo',
  '/music-entertainment': 'music',
  '/music': 'music',
  '/dystinct': 'music',
  '/sponsors': 'sponsors',
  '/sponsors-info': 'sponsorsInfo',
  '/contact': 'contact',
  '/event-schedule': 'eventSchedule',
  '/event-map': 'eventMap',
  '/maps-directions': 'mapsDirections',
  '/privacy-policy': 'privacyPolicy',
  '/cpra-notice': 'privacyPolicy',
  '/accessibility': 'about',
  '/terms-of-use': 'termsOfUse',
  '/ticket-terms': 'ticketTerms',
};

/* ── JSON-LD Structured Data ──────────────────────────── */

const locationLD = {
  '@type': 'Place',
  name: 'OC Fair & Event Center',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '88 Fair Dr',
    addressLocality: 'Costa Mesa',
    addressRegion: 'CA',
    postalCode: '92626',
    addressCountry: 'US',
  },
};

const organizerLD = {
  '@type': 'Organization',
  name: 'OC MENA Festival',
  url: `${SITE_URL}/`,
};

const performerLD = { '@type': 'MusicGroup', name: 'Dystinct' };

const socialLinks = [
  'https://www.instagram.com/ocmenafestival',
  'https://www.facebook.com/ocmenafestival',
  'https://www.tiktok.com/@ocmenafestival',
];

const festivalLD = {
  '@context': 'https://schema.org',
  '@type': 'Festival',
  '@id': `${SITE_URL}/#festival`,
  name: 'OC MENA Festival',
  url: `${SITE_URL}/`,
  startDate: '2026-06-19',
  endDate: '2026-06-21',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  eventStatus: 'https://schema.org/EventScheduled',
  location: locationLD,
  image: [OG_IMAGE],
  description: 'OC MENA Festival brings Middle Eastern and North African culture to Orange County with live music, food, rides, bazaars, and immersive experiences.',
  performer: performerLD,
  organizer: organizerLD,
};

const ticketsEventLD = {
  '@context': 'https://schema.org',
  '@type': 'Event',
  '@id': `${SITE_URL}/tickets#event`,
  name: 'OC MENA Festival Tickets',
  url: `${SITE_URL}/tickets`,
  startDate: '2026-06-19',
  endDate: '2026-06-21',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  eventStatus: 'https://schema.org/EventScheduled',
  location: locationLD,
  image: [OG_IMAGE],
  description: 'OC MENA Festival celebrates Middle Eastern and North African culture with live music, food, rides, bazaars, and immersive experiences in Orange County.',
  performer: performerLD,
  organizer: organizerLD,
  offers: {
    '@type': 'Offer',
    url: `${SITE_URL}/tickets`,
    availability: 'https://schema.org/InStock',
  },
};

const vendorsLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/vendors`,
      url: `${SITE_URL}/vendors`,
      name: 'Vendor Registration | OC MENA Festival',
      description: 'Apply to become a vendor at OC MENA Festival in Costa Mesa, CA. Food vendors, bazaar booths, and cultural marketplace opportunities available.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'OC MENA Festival',
      url: `${SITE_URL}/`,
      logo: LOGO_URL,
      sameAs: socialLinks,
    },
  ],
};

const aboutPageLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      '@id': `${SITE_URL}/about`,
      url: `${SITE_URL}/about`,
      name: 'About OC MENA Festival',
      description: 'Learn about OC MENA Festival, a large-scale cultural celebration in Costa Mesa, CA showcasing Middle Eastern and North African music, food, art, and community.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'OC MENA Festival',
      url: `${SITE_URL}/`,
      logo: LOGO_URL,
      sameAs: socialLinks,
    },
  ],
};

const organizationLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'OC MENA Festival',
  url: `${SITE_URL}/`,
  logo: LOGO_URL,
  description: 'OC MENA Festival is a cultural festival celebrating Middle Eastern and North African music, food, arts, and community in Orange County, California.',
  foundingDate: '2026',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Costa Mesa',
    addressRegion: 'CA',
    addressCountry: 'US',
  },
  sameAs: socialLinks,
};

/* ── Indexed Page Configuration ───────────────────────── */

const PAGES = {
  '/': {
    title: 'OC MENA Festival | June 19-21, 2026 | Costa Mesa, CA',
    jsonLd: festivalLD,
  },
  '/tickets': {
    title: 'OC MENA Festival | Tickets',
    jsonLd: ticketsEventLD,
  },
  '/festival-tickets': {
    title: 'OC MENA Festival | Festival Tickets',
    jsonLd: ticketsEventLD,
  },
  '/amphitheater-tickets': {
    title: 'OC MENA Festival | Amphitheater Tickets',
    jsonLd: ticketsEventLD,
  },
  '/amphitheater-tickets-saturday': {
    title: 'OC MENA Festival | Amphitheater Tickets | Saturday',
    jsonLd: ticketsEventLD,
  },
  '/amphitheater-tickets-sunday': {
    title: 'OC MENA Festival | Amphitheater Tickets | Sunday',
    jsonLd: ticketsEventLD,
  },
  '/amphitheater-tickets-v2': {
    title: 'OC MENA Festival | Amphitheater Tickets',
    jsonLd: ticketsEventLD,
  },
  '/vendors': {
    title: 'OC MENA Festival | Vendors',
    jsonLd: vendorsLD,
  },
  '/bazaar-vendor': {
    title: 'OC MENA Festival | Bazaar Vendor',
    jsonLd: vendorsLD,
  },
  '/food-vendor': {
    title: 'OC MENA Festival | Food Vendor',
    jsonLd: vendorsLD,
  },
  '/about': {
    title: 'OC MENA Festival | About',
    jsonLd: aboutPageLD,
  },
  '/what-is-mena': {
    title: 'OC MENA Festival | What is MENA',
    jsonLd: organizationLD,
  },
  '/faq': {
    title: 'OC MENA Festival | FAQ',
    jsonLd: organizationLD,
  },
  '/festival': {
    title: 'OC MENA Festival | Festival',
    jsonLd: organizationLD,
  },
  '/music-entertainment': {
    title: 'OC MENA Festival | Music & Entertainment',
    description: 'Discover live music and entertainment at OC MENA Festival, featuring artists, comedians, and DJs all weekend while you shop, eat, and explore.',
    jsonLd: organizationLD,
  },
  '/music': {
    title: 'OC MENA Festival | Music & Entertainment',
    description: 'Discover live music and entertainment at OC MENA Festival, featuring artists, comedians, and DJs all weekend while you shop, eat, and explore.',
    jsonLd: organizationLD,
  },
  '/dystinct': {
    title: 'OC MENA Festival | Dystinct',
    jsonLd: organizationLD,
  },
  '/sponsors': {
    title: 'OC MENA Festival | Sponsors',
    jsonLd: organizationLD,
  },
  '/sponsors-info': {
    title: 'OC MENA Festival | Sponsors Info',
    jsonLd: organizationLD,
  },
  '/contact': {
    title: 'OC MENA Festival | Contact',
    jsonLd: organizationLD,
  },
  '/event-schedule': {
    title: 'OC MENA Festival | Event Schedule',
    jsonLd: organizationLD,
  },
  '/event-map': {
    title: 'OC MENA Festival | Event Map',
    jsonLd: organizationLD,
  },
  '/maps-directions': {
    title: 'OC MENA Festival | Maps & Directions',
    jsonLd: organizationLD,
  },
  '/privacy-policy': {
    title: 'OC MENA Festival | Privacy Policy',
    jsonLd: organizationLD,
  },
  '/cpra-notice': {
    title: 'OC MENA Festival | CPRA Notice',
    jsonLd: organizationLD,
  },
  '/accessibility': {
    title: 'OC MENA Festival | Accessibility',
    jsonLd: organizationLD,
  },
  '/terms-of-use': {
    title: 'OC MENA Festival | Terms of Use',
    jsonLd: organizationLD,
  },
  '/ticket-terms': {
    title: 'OC MENA Festival | Ticket Terms',
    jsonLd: organizationLD,
  },
};

/* ── Noindex / Nofollow Pages ─────────────────────────── */

const NOINDEX_PAGES = new Set([
  '/cart',
  '/checkout',
  '/checkout-v2',
  '/dashboard',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/scanner',
  '/success',
  '/order-success',
  '/app',
  '/admin',
  '/vendor-dashboard',
  '/vendor-pending',
  '/coordinator',
  '/transfer/accept',
  '/cms-login',
  '/cms',
  '/keemokazi-giveaway-rules',
  '/seat-designer',
]);

const NOINDEX_TITLES = {
  '/cart': 'Cart',
  '/checkout': 'Checkout',
  '/checkout-v2': 'Checkout',
  '/dashboard': 'Dashboard',
  '/login': 'Login',
  '/signup': 'Sign Up',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/scanner': 'Scanner',
  '/success': 'Success',
  '/order-success': 'Order Success',
  '/app': 'App',
  '/admin': 'Admin',
  '/vendor-dashboard': 'Vendor Dashboard',
  '/vendor-pending': 'Vendor Pending',
  '/coordinator': 'Coordinator',
  '/transfer/accept': 'Transfer',
  '/cms-login': 'CMS Login',
  '/cms': 'CMS',
  '/keemokazi-giveaway-rules': 'Keemokazi Giveaway Rules',
  '/seat-designer': 'Seat Designer',
};

/* ── Default fallback description ─────────────────────── */
const DEFAULT_DESC = 'OC MENA Festival brings Middle Eastern & North African culture to Orange County with live music, food, rides, bazaars, & immersive experiences.';
const DEFAULT_KEYWORDS = 'OC MENA Festival, Orange County festival, Costa Mesa events, Middle Eastern festival California, North African festival, Dystinct concert, Pacific Amphitheatre, MENA culture event';

/* ── Component ────────────────────────────────────────── */

const PageMetaRouter = () => {
  const { pathname } = useLocation();

  // Read CMS overrides
  const cms = getCmsData();
  const cmsKey = PATH_TO_CMS_KEY[pathname];
  const cmsMeta = cmsKey ? (cms.seoMetadata?.pages?.[cmsKey] || {}) : {};
  const defaultShareImage = `${SITE_URL}/og-image.jpg`;

  const isNoindex = NOINDEX_PAGES.has(pathname);
  const config = PAGES[pathname];
  const jsonLd = config ? config.jsonLd : null;
  const configTitle = config ? config.title : 'OC MENA Festival';

  // Code is the source of truth for titles — CMS mirrors it, not the other way around
  const title = isNoindex
    ? `OC MENA Festival | ${NOINDEX_TITLES[pathname] || 'Page'}`
    : (configTitle || cmsMeta.title || 'OC MENA Festival');
  const description = cmsMeta.description || DEFAULT_DESC;
  const keywords = cmsMeta.keywords || DEFAULT_KEYWORDS;
  const ogTitle = cmsMeta.ogTitle || title;
  const ogDescription = cmsMeta.ogDescription || description;
  const shareImage = cmsMeta.shareImage || defaultShareImage;
  const fullImageUrl = shareImage.startsWith('http') ? shareImage : `${SITE_URL}${shareImage}`;
  const robots = isNoindex ? 'noindex, nofollow' : (cmsMeta.robots || 'index, follow');
  const canonical = cmsMeta.canonical || (pathname === '/' ? `${SITE_URL}/` : `${SITE_URL}${pathname}`);
  const jsonLdString = (!isNoindex && jsonLd) ? JSON.stringify(jsonLd) : '';

  // Directly inject JSON-LD into <head> via useEffect
  useEffect(() => {
    const old = document.getElementById('seo-jsonld');
    if (old) old.remove();

    if (jsonLdString) {
      const script = document.createElement('script');
      script.id = 'seo-jsonld';
      script.type = 'application/ld+json';
      script.textContent = jsonLdString;
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById('seo-jsonld');
      if (el) el.remove();
    };
  }, [jsonLdString]);

  // Directly set document.title — Helmet is unreliable for this
  useEffect(() => {
    document.title = title;
  }, [title]);

  // Directly inject/update canonical + OG meta into <head> via useEffect
  useEffect(() => {
    const upsertLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el); }
      el.href = href;
    };
    const upsertMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    upsertLink('canonical', canonical);
    upsertMeta('name', 'title', title);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:title', ogTitle);
    upsertMeta('property', 'og:description', ogDescription);
    upsertMeta('property', 'og:image', fullImageUrl);
    upsertMeta('name', 'twitter:url', canonical);
    upsertMeta('name', 'twitter:title', ogTitle);
    upsertMeta('name', 'twitter:description', ogDescription);
    upsertMeta('name', 'twitter:image', fullImageUrl);
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', robots);
  }, [title, canonical, ogTitle, ogDescription, fullImageUrl, description, keywords, robots]);

  if (isNoindex) {
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  if (!config) return null;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <link rel="canonical" href={canonical} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robots} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:url" content={canonical} />
    </Helmet>
  );
};

export default PageMetaRouter;
